/** Planning · orçamento versionado e linhas mensais (spec 02 §11.5 · telas 8.1/8.2). */
import { z } from 'zod'
import { codigoDeMoeda, uuid } from './comum.js'
import { dinheiroNaoNegativo } from './contas-pagar.js'
import type { ContaContabil } from './plano-contas.js'
import type { RegraDeComissao } from './comissoes.js'

export const estadoDaVersaoDeOrcamento = z.enum([
  'rascunho', 'submetida', 'aprovada', 'bloqueada', 'fechada',
])
export type EstadoDaVersaoDeOrcamento = z.infer<typeof estadoDaVersaoDeOrcamento>

/** DS-622: o `financeiro` é o resultado financeiro (6.1 soma, 6.2 subtrai), fora dos Gastos. */
export const escopoDeOrcamento = z.enum(['receita', 'gastos', 'comissao', 'financeiro'])
export type EscopoDeOrcamento = z.infer<typeof escopoDeOrcamento>

export const calendarioDeOrcamento = z.enum(['vendas', 'faturamento', 'recebimento'])
export type CalendarioDeOrcamento = z.infer<typeof calendarioDeOrcamento>

export const entradaDeNovoOrcamento = z.object({
  ano: z.number().int().min(2000).max(2200),
  moeda: codigoDeMoeda.default('BRL'),
})
export type EntradaDeNovoOrcamento = z.infer<typeof entradaDeNovoOrcamento>

export const entradaDeNovaVersaoDeOrcamento = z.object({
  rotulo: z.string().trim().min(1, 'Informe o rótulo da versão.').max(160),
  derivarDe: uuid.nullable().optional(),
})
export type EntradaDeNovaVersaoDeOrcamento = z.infer<typeof entradaDeNovaVersaoDeOrcamento>

export const motivoDeTransicaoDeOrcamento = z.object({
  motivo: z.string().trim().min(1, 'Informe o motivo.').max(500),
})
export type MotivoDeTransicaoDeOrcamento = z.infer<typeof motivoDeTransicaoDeOrcamento>

export const entradaDeFechamentoDePeriodo = z.object({ mes: z.number().int().min(1).max(12) })
export const entradaDeReaberturaDePeriodo = entradaDeFechamentoDePeriodo.extend({
  motivo: z.string().trim().min(1, 'Informe o motivo.').max(500),
})

/**
 * De onde veio a célula (PK-11, D-130): digitada, sugerida pela premissa da rubrica, ou sugerida e
 * depois mudada à mão — a ajustada nunca mais é recalculada, até "Voltar à sugestão".
 */
export const origemDaCelula = z.enum(['manual', 'derivada', 'ajustada'])
export type OrigemDaCelula = z.infer<typeof origemDaCelula>

/** "Fatura N meses depois, em P parcelas" e o prazo de recebimento do catálogo (PK-11, D-130). */
export const premissaDeReceita = z.object({
  defasagemMeses: z.number().int().min(0, 'De 0 a 11 meses.').max(11, 'De 0 a 11 meses.'),
  parcelas: z.number().int().min(1, 'De 1 a 12 parcelas.').max(12, 'De 1 a 12 parcelas.'),
  perfilPrazoId: uuid,
})
export type PremissaDeReceita = z.infer<typeof premissaDeReceita>

export const entradaDeLinhaDeOrcamento = z.object({
  rubrica: z.string().trim().min(1, 'Informe o nome da linha.').max(160),
  contaContabilId: uuid.nullable(),
  centroCustoId: uuid.nullable(),
  itemContabilId: uuid.nullable(),
  /** Comportamento (D-178): Fixo ou Variável. Não é "% da receita". */
  variavel: z.boolean(),
  /**
   * Sombra técnica (DS-624), sem semântica de cálculo: o servidor grava `variavel ? 'percentual_receita' : null`,
   * qualquer que seja o valor enviado, porque o CHECK da 0060 continua para a imagem anterior voltar sem erro.
   */
  driver: z.literal('percentual_receita').nullable().optional(),
  valores: z.array(dinheiroNaoNegativo).length(12),
  /** Só no Faturamento e no Recebimento. Ausente = tudo digitado. */
  origens: z.array(origemDaCelula).length(12).optional(),
  /** Só em Vendas. Ausente = a premissa gravada fica como está; `null` = desamarrar. */
  premissa: premissaDeReceita.nullable().optional(),
  /**
   * Só em Vendas: a chave que a linha tinha antes de ser renomeada ou reclassificada. A premissa e
   * as células ajustadas vão junto — renomear não é desamarrar.
   */
  chaveAnterior: z.object({
    rubrica: z.string().trim().min(1).max(160),
    contaContabilId: uuid.nullable(),
    centroCustoId: uuid.nullable(),
    itemContabilId: uuid.nullable(),
  }).optional(),
})
export type EntradaDeLinhaDeOrcamento = z.infer<typeof entradaDeLinhaDeOrcamento>

export const entradaDeLinhasDeOrcamento = z.object({
  escopo: escopoDeOrcamento,
  calendario: calendarioDeOrcamento.nullable(),
  linhas: z.array(entradaDeLinhaDeOrcamento).max(500),
  /** Amarrar uma rubrica que já tem Faturamento ou Recebimento digitado substitui o digitado. */
  confirmarAmarracao: z.boolean().optional(),
}).superRefine((entrada, contexto) => {
  const coerente = entrada.escopo === 'receita' ? entrada.calendario !== null : entrada.calendario === null
  if (!coerente) contexto.addIssue({
    code: 'custom', path: ['calendario'], message: 'Calendário pertence somente ao escopo Receita.',
  })
  const derivavel = entrada.calendario === 'faturamento' || entrada.calendario === 'recebimento'
  entrada.linhas.forEach((linha, indice) => {
    if ((linha.premissa !== undefined || linha.chaveAnterior !== undefined) && entrada.calendario !== 'vendas') contexto.addIssue({
      code: 'custom', path: ['linhas', indice, 'premissa'], message: 'A premissa pertence à linha de Vendas.',
    })
    if (!derivavel && linha.origens?.some((origem) => origem !== 'manual') === true) contexto.addIssue({
      code: 'custom', path: ['linhas', indice, 'origens'],
      message: 'Só Faturamento e Recebimento têm célula sugerida.',
    })
  })
})
export type EntradaDeLinhasDeOrcamento = z.infer<typeof entradaDeLinhasDeOrcamento>

export interface AgregadosDoOrcamento {
  readonly vendas: string | null
  readonly faturamento: string | null
  readonly recebimento: string | null
  /** Gastos + comissão: os escopos permanecem partições no grid. O resultado financeiro não é gasto (DS-622). */
  readonly gastos: string | null
  /** DS-622: o resultado financeiro orçado, rendimentos menos juros e tarifas (o sinal vem da conta). */
  readonly financeiro: string | null
  /**
   * O resultado econômico: Faturamento − gastos de RESULTADO + resultado financeiro, nunca Vendas − gastos. O
   * investimento (conta de natureza `investimento`) fica nos `gastos` e fora daqui (DS-609, D-169).
   */
  readonly resultado: string | null
}

/**
 * D-178, DS-623: o estado do Calculado. As pendências (sem base, sem regra, regra ambígua, valor defasado) recusam
 * Submeter e Aprovar; a divergência (a regra vigente é outra que a congelada) só avisa.
 */
export interface ResumoDoCalculo {
  readonly linhasCalculadas: number
  readonly pendencias: number
  readonly divergencias: number
}

export interface VersaoDeOrcamento {
  readonly id: string
  readonly rotulo: string
  readonly estado: EstadoDaVersaoDeOrcamento
  readonly baseline: boolean
  readonly origemVersaoId: string | null
  readonly origemRotulo: string | null
  readonly motivoTransicao: string | null
  readonly updatedAt: string
  readonly updatedByNome: string | null
  readonly agregados: AgregadosDoOrcamento
  readonly diferencaDaOrigem: AgregadosDoOrcamento | null
  /** Ausente na API anterior à v1.6.0. */
  readonly calculo?: ResumoDoCalculo
}

export interface FechamentoDePeriodo {
  readonly mes: number
  readonly quando: string
  readonly fechadoPorNome: string
}

export interface CapacidadesDoOrcamento {
  readonly podeCriar: boolean
  readonly podeAprovar: boolean
  /** `criar` + o marcador Excluir: a exclusão do rascunho é física (BACKLOG 4.209). */
  readonly podeExcluir: boolean
  readonly podeAdministrar: boolean
  readonly veValoresFinanceiros: boolean
  readonly podeEditarRegrasDeComissao: boolean
}

export interface RespostaDosOrcamentos {
  readonly data: {
    readonly id: string
    readonly entityId: string
    readonly ano: number
    readonly moeda: string
    readonly versoes: readonly VersaoDeOrcamento[]
    readonly fechamentos: readonly FechamentoDePeriodo[]
  } | null
  readonly meta: CapacidadesDoOrcamento
}

export const estadoDoCalculo = z.enum(['ok', 'divergente', 'sem_regra', 'regra_ambigua', 'defasada', 'sem_base'])
export type EstadoDoCalculo = z.infer<typeof estadoDoCalculo>
/** Os estados que recusam Submeter e Aprovar. */
export const ESTADOS_PENDENTES_DO_CALCULO: readonly EstadoDoCalculo[] = ['sem_base', 'sem_regra', 'regra_ambigua', 'defasada']

/** Como a célula Calculada nasceu (D-178): base × regra = valor. A explicação que a tela mostra no detalhe. */
export interface CalculoDaCelula {
  readonly tributo: string
  /** O Faturamento total orçado da versão no mês. */
  readonly base: string | null
  /** A fração da regra congelada (0.006500 = 0,65%). */
  readonly percentual: string
  readonly aliquotaId: string
  readonly fonte: string | null
  readonly vigenciaDe: string | null
  readonly vigenciaAte: string | null
  readonly valorEsperado: string | null
  /** A regra vigente hoje no cadastro, quando única; difere da congelada quando o estado é `divergente`. */
  readonly percentualVigente: string | null
  /** DS-629: a fonte da regra vigente hoje, quando única (a congelada tem a dela em `fonte`). Ausente na API anterior à v1.6.1. */
  readonly fonteVigente?: string | null
  readonly estado: EstadoDoCalculo
}

export interface LinhaDeOrcamento {
  readonly escopo: EscopoDeOrcamento
  readonly calendario: CalendarioDeOrcamento | null
  readonly rubrica: string
  readonly contaContabilId: string | null
  readonly contaContabilCodigo: string | null
  readonly contaContabilNome: string | null
  readonly contaPaiNome: string | null
  /** O código da conta-pai, para a âncora do Plano de Contas na linha de grupo (D-136, DS-538). */
  readonly contaPaiCodigo: string | null
  readonly centroCustoId: string | null
  readonly centroCustoNome: string | null
  readonly itemContabilId: string | null
  readonly itemContabilNome: string | null
  /** Comportamento (D-178): Fixo ou Variável, usado pelo break-even. */
  readonly variavel: boolean
  /** Sombra técnica (DS-624), sem semântica; a tela não a mostra. */
  readonly driver: 'percentual_receita' | null
  /** Método (D-178): `calculado` é a linha de Gastos derivada da regra tributária. Ausente na API anterior. */
  readonly metodo?: 'manual' | 'calculado'
  /** Por mês, na ordem de `valores`; só na linha calculada. */
  readonly calculo?: readonly (CalculoDaCelula | null)[] | null
  readonly valores: readonly (string | null)[]
  /** Uma por mês, na ordem de `valores`. */
  readonly origens: readonly OrigemDaCelula[]
  /** Só na linha de Vendas amarrada. */
  readonly premissa: PremissaDaLinhaDeOrcamento | null
  readonly fy: string | null
}

export interface PremissaDaLinhaDeOrcamento extends PremissaDeReceita {
  readonly prazoNome: string
  readonly prazoDias: number
}

export interface RespostaDasLinhasDeOrcamento {
  readonly versao: VersaoDeOrcamento
  readonly moeda: string
  readonly editavel: boolean
  readonly capacidades: CapacidadesDoOrcamento
  readonly agregados: AgregadosDoOrcamento
  readonly linhas: readonly LinhaDeOrcamento[]
  /** Ausente na API anterior à v1.6.0. */
  readonly calculo?: ResumoDoCalculo
}

/** Opções necessárias à 8.2; não concede acesso aos cadastros ou ao uso de cada conta. */
export interface ApoioAsLinhasDoOrcamento {
  readonly contas: readonly (Pick<ContaContabil, 'id' | 'codigo' | 'nome' | 'paiId' | 'natureza' | 'tipo' | 'dreGrupo'> & {
    /** DS-622: a chave técnica; o escopo Financeiro aceita só a 6.1 e a 6.2 por ela. */
    readonly chaveSistema?: string | null
  })[]
  readonly centros: readonly { id: string; rotulo: string }[]
  readonly itens: readonly { id: string; rotulo: string }[]
  /** Catálogo de prazos de recebimento, para a premissa da receita (PK-11). */
  readonly prazos: readonly { id: string; nome: string; dias: number }[]
  /** O Tipo de Empresa de referência sugere o prazo da premissa. */
  readonly tiposDeEmpresa: readonly { id: string; nome: string; prazoPadraoId: string }[]
  readonly matriz: MatrizDeComissaoOrcamentaria
  readonly regras: readonly Pick<RegraDeComissao, 'id' | 'nome' | 'baseCalculo' | 'percentual' | 'valorFixo' | 'moeda' | 'gatilho'>[]
  /** D-178: conta de dedução → tributo da nota que ela representa (pela chave técnica). Ausente na API anterior. */
  readonly tributosDasContas?: Readonly<Record<string, string>>
}

// ------------------------------------------------------------------ os atos com prévia (D-178, DS-623)

export const atoDoOrcamento = z.enum(['converter_calculado', 'voltar_manual', 'atualizar_calculos', 'aplicar_classificacao'])
export type AtoDoOrcamento = z.infer<typeof atoDoOrcamento>

export const chaveDaLinhaDeOrcamento = z.object({
  rubrica: z.string().trim().min(1).max(160),
  contaContabilId: uuid.nullable(),
  centroCustoId: uuid.nullable(),
  itemContabilId: uuid.nullable(),
})
export type ChaveDaLinhaDeOrcamento = z.infer<typeof chaveDaLinhaDeOrcamento>

export const entradaDaPreviaDeAto = z.object({
  ato: atoDoOrcamento,
  /** A linha de Gastos alvo; ausente em "Atualizar cálculos" (a versão inteira) e em "Aplicar classificação". */
  linha: chaveDaLinhaDeOrcamento.nullable().optional(),
}).superRefine((entrada, contexto) => {
  const precisaDeLinha = entrada.ato === 'converter_calculado' || entrada.ato === 'voltar_manual'
  if (precisaDeLinha && (entrada.linha === undefined || entrada.linha === null)) contexto.addIssue({
    code: 'custom', path: ['linha'], message: 'Escolha a linha.',
  })
})
export type EntradaDaPreviaDeAto = z.infer<typeof entradaDaPreviaDeAto>

export const entradaDoAtoDeOrcamento = z.object({
  ato: atoDoOrcamento,
  linha: chaveDaLinhaDeOrcamento.nullable().optional(),
  /** A impressão digital da prévia que a pessoa viu: se algo mudou desde então, o ato é recusado (409). */
  impressaoDigital: z.string().min(16).max(128),
})
export type EntradaDoAtoDeOrcamento = z.infer<typeof entradaDoAtoDeOrcamento>

export interface LadoDaCelulaNaPrevia {
  readonly valor: string
  readonly origem: OrigemDaCelula
  readonly variavel: boolean
  readonly centroCustoNome: string | null
  /** O percentual da regra, quando a célula é (ou passa a ser) calculada. */
  readonly percentual: string | null
}

export interface CelulaNaPrevia {
  readonly mes: number
  readonly antes: LadoDaCelulaNaPrevia
  readonly depois: LadoDaCelulaNaPrevia
}

export interface LinhaNaPrevia {
  readonly rubrica: string
  readonly contaContabilCodigo: string | null
  readonly contaContabilNome: string | null
  readonly celulas: readonly CelulaNaPrevia[]
}

export interface PrevisaoDoAto {
  readonly ato: AtoDoOrcamento
  /** Falso quando há pendência que impede aplicar (a lista diz qual). */
  readonly aplicavel: boolean
  readonly impressaoDigital: string
  readonly pendencias: readonly { readonly rubrica: string; readonly mes: number | null; readonly motivo: string }[]
  readonly linhas: readonly LinhaNaPrevia[]
  /** Quantas células mudam (valor, origem, marca, centro ou regra). */
  readonly celulasAlteradas: number
  readonly totalAntes: string
  readonly totalDepois: string
  /** totalDepois − totalAntes: o único efeito em valor que o ato pode ter. */
  readonly delta: string
}

export interface ParceiroDaMatrizDeComissao {
  readonly id: string
  readonly nome: string
  readonly tipoParceiroId: string
}

export interface CatalogosDaMatrizDeComissao {
  readonly produtos: readonly { id: string; nome: string }[]
  readonly tiposParceiro: readonly { id: string; nome: string }[]
  readonly tiposCliente: readonly { id: string; nome: string; setor: 'Público' | 'Privado' }[]
}

export interface MatrizDeComissaoOrcamentaria {
  readonly produtos: CatalogosDaMatrizDeComissao['produtos']
  readonly parceiros: readonly {
    id: string
    nome: string
    tipoNome: string
    celulas: readonly {
      produtoId: string
      alternativas: readonly {
        regra: ApoioAsLinhasDoOrcamento['regras'][number] | null
        clientes: readonly { id: string | null; nome: string }[]
      }[]
    }[]
  }[]
}
